
/* eslint-disable import/no-named-export */

import React, {
    useRef,
    useCallback,
    useState
} from 'react';

import RIQModal from '../../../components/RIQModal.jsx';
import RIQButton from '../../../components/RIQButton.jsx';

import {
    useSetCurationConfigState,
    useCurationConfigValue,
    useSerializeConfigurations
} from '../../../hooks/widgetConfiguration.js';

import {
    useListAccountSavedSearches,
    useSaveLandscapeListeningQuery
} from './useListeningQueryPersistence.js';

import useValidateSavedSearchName from './useValidateSavedSearchName.js';

import {
    listeningDefinitions
} from '../components/enums/listeningDefinitions.jsx';

const {
    title,
    footer
} = listeningDefinitions['save-as-saved-search'];

function useCreateSearch(newSearchName, onSettled) {
    const setEditingListeningQueryId = useSetCurationConfigState('editingListeningQueryId');
    const companyId = useCurationConfigValue('companyId');
    const {mutate: saveCurationConfiguration} = useSaveLandscapeListeningQuery();
    return useSerializeConfigurations(allProps => {
        saveCurationConfiguration({
            name: newSearchName,
            definition: _.omit(allProps, 'name', 'editingListeningQueryId'),
            companyId: companyId,
            listeningQueryTypeId: RivalIQ.enums.listeningQueryTypes.SavedSearch.id
        }, {
            onSettled: (data, error) => {
                if (data) {
                    setEditingListeningQueryId(data.id);
                }
                onSettled(data, error);
            }
        });
    });
}

function SaveAsSavedSearchModal({isModalVisible, onModalDismissed, isSaving, setIsSaving, postSaveCallback}) {
    const inputRef = useRef();
    const [newSearchName, setNewSearchName] = useState('');
    const isInvalidSavedSearch = useValidateSavedSearchName(newSearchName);
    const [changedSearchText, setChangedSearchText] = useState(false);

    const persistToServer = useCreateSearch(newSearchName, postSaveCallback);

    function onNewSearchNameChange(e) {
        setNewSearchName(e.target.value);
        setChangedSearchText(true);
    }

    function onSaveClick() {
        setIsSaving(true);
        persistToServer();
    }

    function onKeyDown(e) {
        if (e.key === 'Enter' && !isInvalidSavedSearch) {
            onSaveClick();
        }
    }

    const plan = useCurationConfigValue('plan');
    const numSavedSearchesAllowed = plan?.max_listening_saved_searches;
    const {data: allSavedSearches} = useListAccountSavedSearches();
    const numAllSavedSearches = _.size(allSavedSearches);
    const canCreateMoreSearches = Boolean(numSavedSearchesAllowed && numAllSavedSearches < numSavedSearchesAllowed);

    const footerText = footer(numAllSavedSearches, numSavedSearchesAllowed);

    return (
        <RIQModal
            initialFocus={inputRef}
            onModalDismissed={onModalDismissed}
            showModal={isModalVisible}
            title={title}
            variant='small'>
            <div className='tlw-m-10'>
                <input
                    className='tlw-mr-20 tlw-w-full tlw-text-ellipsis tlw-rounded-sm tlw-border tlw-border-solid tlw-border-gray-600 tlw-py-3 tlw-pl-6 tlw-pr-6 tlw-text-md tlw-leading-20 tlw-shadow-sm tlw-outline-none focus_tlw-border-blue-400 focus_tlw-shadow-inner focus_tlw-outline-none disabled_tlw-pointer-events-none disabled_tlw-cursor-not-allowed disabled_tlw-opacity-65 disabled_tlw-shadow-none'
                    ref={inputRef}
                    value={newSearchName || ''}
                    onChange={onNewSearchNameChange}
                    onKeyDown={onKeyDown}
                />
                {changedSearchText && isInvalidSavedSearch &&
                <div className='tlw-text-sm tlw-text-red-500'>
                    {isInvalidSavedSearch.error}
                </div>
                }
            </div>
            <div
                className='tlw-m-0 tlw-flex tlw-grow tlw-flex-col tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300 tlw-p-7.5 tlw-text-base tlw-leading-22 tlw-text-gray-900 sm_tlw-flex-row sm_tlw-items-center'
            >
                <div
                    className='tlw-order-last tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap tlw-pt-5 tlw-text-sm sm_tlw-order-first sm_tlw-pt-0'
                >
                    {footerText}
                </div>

                <RIQButton
                    variant='primary'
                    className='tlw-w-full tlw-px-6 tlw-py-3 sm_tlw-ml-auto sm_tlw-w-auto'
                    onClick={onSaveClick}
                    disabled={isSaving || (isInvalidSavedSearch && canCreateMoreSearches)}
                >
                    Save
                </RIQButton>
            </div>
        </RIQModal>
    );
}

export default function useSaveAsSavedSearchModal(setIsSaving, postSaveCallback, isSaving) {

    const [isModalVisible, setIsModalVisible] = useState(false);

    const showSaveAsSavedSearchModal = useCallback(() => {
        setIsModalVisible(true);
    }, []);

    const modalComponent = <SaveAsSavedSearchModal
        isModalVisible={isModalVisible}
        onModalDismissed={() => setIsModalVisible(false)}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        postSaveCallback={postSaveCallback}
    />;

    return {
        showSaveAsSavedSearchModal,
        SaveAsSavedSearchModal: modalComponent
    };
}
